import { useApollo } from "@/apollo/client";
import { BreadcrumbsProvider } from "@/components/Breadcrumbs";
import { CopyrightsProvider } from "@/components/Copyrights";
import DefaultHeader from "@/components/DefaultHeader";
import Loading from "@/components/Loading";
import { ModalProvider } from "@/components/Modal";
import NetworkNotification from "@/components/NetworkNotification";
import { NotificationProvider } from "@/components/Notification";
import { PreviousPathProvider } from "@/components/PreviousPath";
import { RecaptchaV3Provider } from "@/components/RecaptchaV3";
import SelectProfile from "@/components/SelectProfile";
import { useAppInit, useBuildId } from "@/hooks";
import { Layout } from "@/layouts";
import config from "@/lib/config";
import maybeIPad from "@/lib/maybeIPad";
import routes from "@/lib/routes";
import "@/styles/globals.scss";
import { ApolloProvider } from "@apollo/client/react";
import classnames from "classnames";
import Error from "next/error";
import { useRouter } from "next/router";
import Script from "next/script";
import { useEffect, useMemo } from "react";
import "text-security/text-security.css";
import "url-search-params-polyfill";

const cssVariablesIE11Polyfill = {
  __html: `
      if (window.MSInputMethodContext && document.documentMode) {
        var script = "\<script src='https://cdn.jsdelivr.net/gh/nuxodin/ie11CustomProperties@4.1.0/ie11CustomProperties.min.js'><script>";
        document.write(script);
      }
    `,
};

function InnerApp(props) {
  const { loading, user, selectedProfile, deviceInfo } = useAppInit();

  const router = useRouter();
  const showSelectProfile = useMemo(() => {
    if (router.pathname === routes.profilePincodeReset) return false;
    return !loading && user && !selectedProfile;
  }, [loading, user, selectedProfile, router.pathname]);

  const noHeader = useMemo(() => {
    if (router.pathname.includes("/device/activation")) return false;
    return props.noHeader || (user && !selectedProfile);
  }, [user, selectedProfile, props.noHeader]);

  return (
    <div
      id="app"
      className={classnames({
        tablet: deviceInfo?.isTablet || maybeIPad(deviceInfo),
        mobile: deviceInfo?.isMobile,
      })}
    >
      {loading && <Loading fixed />}
      {!loading && (
        <NotificationProvider>
          <Layout noHeader={noHeader} noTransition={props.noTransition}>
            <NetworkNotification />

            {!showSelectProfile && props.children}
            {showSelectProfile && <SelectProfile backToPrevPath />}
          </Layout>
        </NotificationProvider>
      )}
    </div>
  );
}

function SpodApp({ Component, pageProps, router }) {
  const { shouldReload } = useBuildId();

  const client = useApollo(pageProps.initialApolloState);
  const noHeader = Component.layout === "NoHeaderLayout";
  const noTransition = Component.noTransition;

  useEffect(() => {
    const handleRouteChange = async () => {
      if (await shouldReload()) {
        window.location.reload();
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [shouldReload]);

  if (pageProps.error) {
    // TODO: カスタムエラーページ
    return (
      <Error
        statusCode={pageProps.error.statusCode}
        title={pageProps.error.message}
      />
    );
  }

  return (
    <>
      <Script
        key="ie11polyfill"
        dangerouslySetInnerHTML={cssVariablesIE11Polyfill}
        strategy="beforeInteractive"
      />
      <Script
        key="jquery"
        src="https://code.jquery.com/jquery-3.5.1.min.js"
        integrity="sha256-9/aliU8dGd2tb6OSsuzixeV4y/faTqgFtohetphbbj0="
        crossOrigin="anonymous"
      />
      <Script
        key="streaksjs"
        src={config.streaks.js}
        strategy="beforeInteractive"
      />
      <Script key="sbps" type="text/javascript" src={config.sbps.src} />
      <Script key="sbps-3ds" type="text/javascript" src={config.sbps.src3ds} />
      <DefaultHeader metatags={pageProps?.metatags} />
      <ApolloProvider client={client}>
        <PreviousPathProvider>
          <BreadcrumbsProvider>
            <CopyrightsProvider>
              <ModalProvider>
                <RecaptchaV3Provider>
                  <InnerApp
                    {...pageProps}
                    noHeader={noHeader}
                    noTransition={noTransition}
                  >
                    <Component {...pageProps} />
                  </InnerApp>
                </RecaptchaV3Provider>
              </ModalProvider>
            </CopyrightsProvider>
          </BreadcrumbsProvider>
        </PreviousPathProvider>
      </ApolloProvider>
    </>
  );
}

export default SpodApp;
